import React, { useState, useEffect } from "react";
import { Select, Input, Switch, Upload, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import styles from "./Setup.module.css";

const { Option } = Select;
// const API_URL = "http://localhost:8010/proxy"; // Change this to actual API URL when ready
const API_URL =
  "https://uvjwfdf5aa.execute-api.eu-west-1.amazonaws.com/test/setup-configuration-api";
const placeId = "shka17c8h3"; // Will be dynamic in the future

const hookahTypeOptions = [
  "DarkSide",
  "Sherbetli",
  "DailyHookah",
  "Tangers",
  "Adaliya",
  "Starbuzz",
  "AlFakher",
]; // Fixed options

const Setup = () => {
  const [notifications, setNotifications] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [hookahTypes, setHookahTypes] = useState([]); // Preselected from API
  const [coalPreparationTime, setCoalPreparationTime] = useState(null);
  const [coalRefreshTime, setCoalRefreshTime] = useState(null);
  const [coalRefreshCount, setCoalRefreshCount] = useState(null);
  const [numberOfTables, setNumberOfTables] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSetupConfig();
  }, []);

  const fetchSetupConfig = async () => {
    setLoading(true);
    const requestBody = {
      place_id: placeId.trim(),
      action: "read",
    };

    console.log("📤 Sending request to API:", JSON.stringify(requestBody));

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const text = await response.text();
      console.log("📥 Raw API Response:", text);

      const data = JSON.parse(text);
      console.log("📥 Parsed API Response:", data);

      if (response.ok && data.data) {
        setHookahTypes(
          data.data.hookah_types.filter((type) =>
            hookahTypeOptions.includes(type)
          ) || []
        );
        setCoalPreparationTime(
          data.data.hookah_config.coal_preparation_timer_minutes
        );
        setCoalRefreshTime(data.data.hookah_config.coal_refresh_timer_minutes);
        setCoalRefreshCount(data.data.hookah_config.coal_refresh_count);
        setNumberOfTables(data.data.number_of_tables);
        setNotifications(data.data.is_notification_on);
        setCoverImage(data.data.cover_page_s3_url);
        setCoverImage(data.presigned_url || ""); // ✅ Load cover image from API
      } else {
        message.error(
          `Ошибка загрузки конфигурации: ${
            data.message || "Некорректный запрос"
          }`
        );
      }
    } catch (error) {
      console.error("❌ Ошибка при парсинге JSON:", error);
      message.error("Ошибка при обработке ответа от сервера.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = ({ file }) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      setCoverImage(reader.result); // ✅ Update preview image with Base64
    };

    reader.onerror = (error) => {
      console.error("❌ Ошибка загрузки изображения:", error);
      message.error("Ошибка загрузки изображения.");
    };
  };

  const handleHookahChange = (values) => {
    setHookahTypes(values);
  };

  const handleSave = async () => {
    setLoading(true);

    const requestBody = {
      place_id: placeId.trim(),
      action: "create",
      data: {
        hookah_config: {
          coal_preparation_timer_minutes: Number(coalPreparationTime),
          coal_refresh_timer_minutes: Number(coalRefreshTime),
          coal_refresh_count: coalRefreshCount,
        },
        hookah_types: hookahTypes, // Selected hookah types
        number_of_tables: numberOfTables,
        is_notification_on: notifications,
      },
      data_url: coverImage || "", // Cover image URL (will implement base64 conversion later)
    };

    console.log("📤 Sending Save request:", JSON.stringify(requestBody));

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const text = await response.text();
      console.log("📥 Raw API Save Response:", text);

      const data = JSON.parse(text);
      console.log("📥 Parsed API Save Response:", data);

      if (response.ok && data.statusCode === 200) {
        message.success("Настройки успешно сохранены! 🎉");
      } else {
        message.error(
          `Ошибка сохранения: ${data.message || "Некорректный запрос"}`
        );
      }
    } catch (error) {
      console.error("❌ Ошибка при сохранении данных:", error);
      message.error("Ошибка при отправке данных на сервер.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollContainer}>
        <div className={styles.formContainer}>
          <h1 className={styles.title}>Настройки</h1>

          {/* Hookah Type Selection */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Наименование табаков</label>
            <p className={styles.description}>
              Выберите из списка табаков, которые вы используете в работе
            </p>
            <Select
              className={styles.select}
              mode="multiple"
              placeholder="Выбрать"
              value={hookahTypes}
              onChange={handleHookahChange}
              options={hookahTypeOptions.map((type) => ({
                label: type,
                value: type,
              }))}
            />
            <div className={styles.selectedTags}>
              {hookahTypes.map((type) => (
                <span key={type} className={styles.tag}>
                  {type}{" "}
                  <span
                    className={styles.removeTag}
                    onClick={() =>
                      handleHookahChange(hookahTypes.filter((t) => t !== type))
                    }
                  >
                    ✖
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* Number of Tables */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Количество столов</label>
            <p className={styles.description}>
              Укажите сколько столов у вас в зале для гостей
            </p>
            <Input
              type="number"
              className={styles.input}
              placeholder="Укажите количество"
              value={numberOfTables}
              onChange={(e) => setNumberOfTables(e.target.value)}
            />
          </div>

          {/* Coal Preparation Timer */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Таймер для приготовления углей
            </label>
            <Input
              type="number"
              value={coalPreparationTime}
              onChange={(e) => setCoalPreparationTime(e.target.value)}
            />
          </div>

          {/* Coal Refresh Timer */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Таймер для замены углей</label>
            <Input
              type="number"
              value={coalRefreshTime}
              onChange={(e) => setCoalRefreshTime(e.target.value)}
            />
          </div>

          {/* Coal Refresh Count */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Количество замен</label>
            <Input
              type="number"
              className={styles.input}
              value={coalRefreshCount}
              onChange={(e) => setCoalRefreshCount(e.target.value)}
            />
          </div>

          {/* Notifications Toggle */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Включить уведомления</label>
            <p className={styles.description}>
              Звуковые и вибро-оповещения для напоминаний о замене углей
              и начале этапов обслуживания
            </p>
            <div className={styles.switchContainer}>
              <Switch
                checked={notifications}
                onChange={() => setNotifications(!notifications)}
              />
            </div>
          </div>

          {/* Cover Image Upload */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Обложка</label>
            <p className={styles.description}>
              Гости увидят ваше фирменное изображение при регистрации. Макс. 500
              кб.
            </p>
            {/* Centered Container */}
            <div className={styles.uploadContainer}>
              {/* Upload Button */}
              <Upload
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleImageUpload} // ✅ Handle image selection
              >
                <div className={styles.uploadPicture}>
                  <PlusOutlined />
                  <div>Загрузить</div>
                </div>
              </Upload>

              {/* Image Preview (Appears Just Below Upload Button, Centered & Larger) */}
              {coverImage && (
                <img
                  src={coverImage}
                  alt="Cover Preview"
                  className={styles.previewImage}
                />
              )}
            </div>
          </div>

          <Button
            type="primary"
            className={styles.saveButton}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Сохранение..." : "Сохранить"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Setup;
